import { Controller } from "@hotwired/stimulus";
import csrfFetch from "../lib/csrf_fetch";

const markIcon = (icon) =>
  `<i class="fas ${icon} fa-lg my-1 ms-2" aria-hidden="true"></i>`;
// An option's square alone shows the pick, so its mark carries text too
const optionMark = (icon, text) =>
  `${markIcon(icon)}<span class="visually-hidden">${text}</span>`;

// Toggles a select-all question's options, sends the picked ids and paints the reveal
export default class extends Controller {
  static targets = ["option", "submitButton", "nextButton"];
  static outlets = ["quiz-stats"];

  toggle(event) {
    const option = event.currentTarget;
    option.setAttribute("aria-pressed", String(!this._pressed(option)));
    this.submitButtonTarget.disabled = this._picked().length === 0;
  }

  async check() {
    const picked = this._picked();
    const buttons = [...this.optionTargets, this.submitButtonTarget];
    buttons.forEach((button) => {
      button.disabled = true;
    });

    let response;
    try {
      response = await csrfFetch(window.location.pathname, {
        method: "PUT",
        body: JSON.stringify({
          answer: { ids: picked.map((o) => o.id.slice("response-".length)) },
        }),
      });
    } catch {
      // The answer may have landed with only its response lost; a resend is
      // then refused as no answer to the next question, and reload shows the quiz's state
      buttons.forEach((button) => {
        button.disabled = false;
      });
      // Disabling the focused Check button drops focus to <body>
      this.submitButtonTarget.focus();
      return;
    }

    let payload;
    try {
      if (!response.ok) throw new Error();
      payload = await response.json();
    } catch {
      // A refusal, or a followed redirect landing on an HTML page, means
      // the page is stale; reload shows where the quiz now stands
      this.reload();
      return;
    }

    this._mark(payload);
    if (this.hasQuizStatsOutlet) this.quizStatsOutlet.update(payload);

    this.nextButtonTarget.classList.remove("invisible");
    this.nextButtonTarget.focus();
  }

  reload() {
    location.reload();
  }

  _pressed(option) {
    return option.getAttribute("aria-pressed") === "true";
  }

  _picked() {
    return this.optionTargets.filter((o) => this._pressed(o));
  }

  // The server decides correctness; the answers are only for the reveal.
  // Every correct option ticks, picked or missed; only a picked wrong one crosses
  _mark({ correct, answer }) {
    const correctIds = new Set(answer.map((a) => `response-${a.id}`));
    for (const option of this.optionTargets) {
      if (correctIds.has(option.id)) {
        option.classList.add("correct-answer");
        option.insertAdjacentHTML(
          "beforeend",
          optionMark("fa-check", "correct answer"),
        );
      } else if (this._pressed(option)) {
        option.classList.add("incorrect-answer");
        option.insertAdjacentHTML(
          "beforeend",
          optionMark("fa-times", "wrong answer"),
        );
      }
    }

    const button = this.submitButtonTarget;
    button.classList.add(correct ? "correct-answer" : "incorrect-answer");
    button.textContent = correct ? "Correct!" : "Incorrect";
    button.insertAdjacentHTML(
      "beforeend",
      markIcon(correct ? "fa-check" : "fa-times"),
    );
  }
}
